/**
 * Iter89l (2026-02-24) — Rich-Text-Editor MVP-Skelett.
 *
 * Tiptap-basierter Rich-Text-Editor mit Toolbar (Bold/Italic/Listen/Links)
 * und Placeholder-Chip-Insertion. Für `text_block`-Vorlagen (Anreden,
 * Signaturen, Zahlungshinweise etc.).
 *
 * ACHTUNG — Phase 1 Fundament:
 *   • Ist EINE tragfähige Editor-Komponente
 *   • Ist NICHT verdrahtet: Admin-UI, DB-Persistierung `text_block`-
 *     Vorlagen, PDF-Renderer-Integration → alle in Folge-Session.
 *   • Placeholder werden als `${…}`-Text in den HTML-String eingefügt;
 *     die visuelle Chip-Darstellung im Editor ist noch ein simpler
 *     `<code>`-Wrap (echtes Chip-Node folgt in Folge-Session).
 */
import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { Bold, Italic, List, ListOrdered, Link2, Code, Undo2, Redo2 } from "lucide-react";
import { PLACEHOLDER_CATEGORIES } from "./PlaceholderRegistry";

const ToolbarBtn = ({ onClick, active, disabled, children, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    type="button"
    title={title}
    className={`h-8 w-8 rounded-md inline-flex items-center justify-center transition
      ${active ? "bg-[#00E575]/20 text-[#00E575]" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}
      disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

export default function TextBlockEditor({ value = "", onChange, placeholder = "Vorlagen-Text hier…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  const [placeholderPickerOpen, setPlaceholderPickerOpen] = useState(false);

  if (!editor) return null;

  const insertPlaceholder = (key) => {
    editor.chain().focus().insertContent(`<code>\${${key}}</code>&nbsp;`).run();
    setPlaceholderPickerOpen(false);
  };

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link-URL:", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="bg-[#09090B] border border-zinc-800 rounded-xl overflow-hidden" data-testid="text-block-editor">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-zinc-800 bg-zinc-900/40">
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Fett"
        >
          <Bold size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Kursiv"
        >
          <Italic size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Aufzählung"
        >
          <List size={14} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Nummerierte Liste"
        >
          <ListOrdered size={14} />
        </ToolbarBtn>
        <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Link">
          <Link2 size={14} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-zinc-700 mx-1" />
        <div className="relative">
          <button
            type="button"
            onClick={() => setPlaceholderPickerOpen(v => !v)}
            data-testid="text-block-placeholder-btn"
            className="h-8 px-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11.5px] font-semibold inline-flex items-center gap-1"
            title="Platzhalter einfügen"
          >
            <Code size={13} /> Platzhalter
          </button>
          {placeholderPickerOpen && (
            <div
              className="absolute z-30 mt-1 left-0 w-72 max-h-96 overflow-y-auto bg-[#09090B] border border-zinc-700 rounded-lg shadow-xl p-2 space-y-2"
              data-testid="text-block-placeholder-picker"
            >
              {PLACEHOLDER_CATEGORIES.map(cat => (
                <div key={cat.id}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#00E575] px-1 pb-1">{cat.label}</div>
                  <div className="grid grid-cols-1 gap-0.5">
                    {cat.placeholders.map(p => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => insertPlaceholder(p.key)}
                        className="text-left px-2 py-1 rounded hover:bg-zinc-800 text-[11.5px] text-zinc-200 inline-flex items-center gap-2"
                      >
                        <code className="text-[10.5px] text-[#00E575]">${'{' + p.key + '}'}</code>
                        <span className="text-zinc-400 truncate">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          <Undo2 size={14} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          <Redo2 size={14} />
        </ToolbarBtn>
      </div>
      <div className="p-3">
        <EditorContent
          editor={editor}
          className="prose prose-invert prose-sm max-w-none min-h-[120px] focus:outline-none text-[13px] leading-relaxed [&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:outline-none [&_.ProseMirror_code]:bg-[#00E575]/15 [&_.ProseMirror_code]:text-[#00E575] [&_.ProseMirror_code]:px-1.5 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-[11.5px] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-zinc-500 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
        />
      </div>
    </div>
  );
}
